import React, { memo, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { CallLog } from '../../../../types/call-logs';
import './call-logs-detail-call-outcome-select.scss';
import { RootState } from '../../../../../../store/root-reducer';
import SimpleOptionsDropdown, {
  SimpleDropdownOption,
} from '../../../../../../shared/components/simple-options-dropdown/simple-options-dropdown';
import { getCallOutcomeIcon } from '../../../../../settings/components/call-outcomes/components/call-outcome-content/helpers/call-outcome-column-data';

export type CallOutcomeSelectChangeContext = {
  row: CallLog | any;
};

interface IProps {
  row: CallLog | any;
  /**
   * Default behavior: `(outcomeId, callLogId)` from the selected option and `row.id`.
   * Ignored when `onChange` is provided.
   */
  onChangeCallOutcome?: (outcomeId: number, callLogId: number) => void;
  /**
   * Full control: receives the dropdown option + row (e.g. post-call modal local state).
   * When set, this runs instead of `onChangeCallOutcome`.
   */
  onChange?: (
    option: SimpleDropdownOption,
    context: CallOutcomeSelectChangeContext,
  ) => void;
}

const CallLogsDetailCallOutcomeSelect: React.FC<IProps> = memo(
  ({ row, onChangeCallOutcome, onChange }) => {
    const callOutcomeList = useSelector(
      (state: RootState) => state?.dialer?.getCallOutcomeListResponse ?? [],
    );

    const options: SimpleDropdownOption[] = useMemo(
      () =>
        (callOutcomeList ?? []).map((o) => ({
          key: o.id,
          label: String(o.name ?? ''),
          isDefault: Boolean(o.isDefault),
          sentiment: String(o.sentiment ?? ''),
        })),
      [callOutcomeList],
    );

    const selectedKey = row.callOutcome?.id ?? null;
    const fallbackLabel = row.callOutcome?.name;

    const fallbackOption = useMemo((): SimpleDropdownOption | undefined => {
      const co = row.callOutcome;
      if (!co?.id) {
        return undefined;
      }
      return {
        key: co.id,
        label: String(co.name ?? ''),
        isDefault: Boolean(co.isDefault),
        sentiment: String(co.sentiment ?? ''),
      };
    }, [row.callOutcome]);

    const renderOptionIcon = useCallback(
      (opt: SimpleDropdownOption) =>
        getCallOutcomeIcon(
          opt.label,
          Boolean(opt.isDefault),
          String(opt.sentiment ?? ''),
          { width: 16, height: 16 },
        ),
      [],
    );

    const handleChange = useCallback(
      (option: SimpleDropdownOption) => {
        if (onChange) {
          onChange(option, { row });
          return;
        }
        onChangeCallOutcome?.(Number(option.key), row.id);
      },
      [onChange, onChangeCallOutcome, row],
    );

    return (
      <div className="bs-call-logs-detail-call-outcome-select">
        <SimpleOptionsDropdown
          className="bs-call-logs-detail-call-outcome-select__control"
          options={options}
          selectedKey={selectedKey}
          fallbackLabel={fallbackLabel}
          fallbackOption={fallbackOption}
          placeholder="Select an outcome"
          showSearch
          searchPlaceholder="Search"
          renderOptionIcon={renderOptionIcon}
          onChange={handleChange}
        />
      </div>
    );
  },
);

CallLogsDetailCallOutcomeSelect.displayName = 'CallLogsDetailCallOutcomeSelect';

export default CallLogsDetailCallOutcomeSelect;
